// R15b-b3: converted native in place (antd Result/Alert/Button → kit) — this
// shared FS-01/02/03 blocking surface is STATIC-imported by the realTime
// provider and InitRealtimeExperience (APL-11850, v3 parity: a lazy chunk
// can't load under the AAC network blackhole that triggers these pages).
import { APStatusView } from '@/components/ap/status/APStatusView';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/dataHelpers';
import { getAppType } from '@/lib/offline/OfflineAppHelper';
import { CloseCircleBold } from '@assessprep/icons/solar';
import { useTranslate } from '@refinedev/core';

export type RealtimeDBErrorType = 'connection' | 'native_connection' | 'backup';

export const RealtimeDBError = (props: { errorType: RealtimeDBErrorType }) => {
  const { errorType } = props;
  const translate = useTranslate();
  const currentUser = useCurrentUser();
  const embedded = currentUser?.embedded;

  const appType = getAppType();

  const extra = [];
  if (appType === 'web') {
    if (embedded) {
      extra.push(
        <Alert variant="destructive" key="refresh-note" className="inline-block w-auto">
          <AlertDescription>{translate('FirestoreInteractions.refresh_the_page')}</AlertDescription>
        </Alert>
      );
    } else {
      extra.push(
        <a href="/" key="back-home">
          <Button variant="outline">{translate('FirebaseError.back_home')}</Button>
        </a>
      );
      extra.push(
        <Button key="refresh" variant="outline" onClick={() => window.location.reload()}>
          {translate('CommonText.refresh')}
        </Button>
      );
    }
  } else {
    extra.push(
      <Button key="refresh" variant="outline" onClick={() => window.location.reload()}>
        {translate('CommonText.refresh')}
      </Button>
    );
  }

  // FS-01 (connection) and FS-02 (backup) keep dev's copy with the error code baked
  // into the message string. FS-03 (native_connection, APL-11641) has its own title +
  // message: same body as the connection page but a backup-specific title and code.
  const ERROR_VARIANTS: Record<RealtimeDBErrorType, { messageKey: string; titleKey: string }> = {
    connection: {
      messageKey: 'FirestoreInteractions.critical_service_connection_error_student', // FS-01
      titleKey: 'FirestoreInteractions.sorry_something_went_wrong',
    },
    native_connection: {
      // FS-03 — supported native build (Mac >= 13.0.0 / iOS >= 6.0.0) couldn't sign in
      messageKey: 'FirestoreInteractions.critical_native_signin_error_student',
      titleKey: 'FirestoreInteractions.native_signin_failed_title',
    },
    backup: {
      messageKey: 'FirestoreInteractions.critical_error_to_get_data_from_firestore', // FS-02
      titleKey: 'FirestoreInteractions.sorry_something_went_wrong',
    },
  };
  const { messageKey, titleKey } = ERROR_VARIANTS[errorType] ?? ERROR_VARIANTS.connection;

  return (
    <APStatusView
      className="min-h-[60vh]"
      tone="danger"
      icon={<CloseCircleBold />}
      title={translate(titleKey)}
      testId="realtime-db-error"
      description={
        <Alert variant="destructive" className="inline-block w-auto max-w-125 text-start">
          <AlertDescription>{translate(messageKey)}</AlertDescription>
        </Alert>
      }
      actions={extra}
    />
  );
};
